import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../utils/supabase';
import { downloadCsv } from '../../../utils/leads';
import {
  invoiceStatus, isoToday, loadClients, loadInvoices, loadProducts, loadSeller, loadTaxRates, money, receivablesAging,
  revenueByLine, salesTaxReport, shortDate, STATUS_TONES,
} from '../../../utils/invoices';
import { errorBox, inputClass } from '../../AuthLayout';
import InvoiceEditor from './InvoiceEditor';
import { BusinessProfile, btnPrimary, btnSecondary, Card, Clients, Field, Products, Taxes } from './InvoiceSetup';

const TABS = [
  ['list', 'Invoices'], ['clients', 'Clients'], ['products', 'Products'], ['taxes', 'Sales tax'], ['reports', 'Reports'], ['profile', 'Business profile'],
];

/** Admin → Invoices (?section=invoices&tab=…&invoice=<id|new>). One seller (Doggy Canvas LLC) for now. */
export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'list';
  const openId = params.get('invoice');
  const [seller, setSeller] = useState(undefined);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState('');

  const nav = (next) => {
    const p = { section: 'invoices' };
    if (next.tab && next.tab !== 'list') p.tab = next.tab;
    if (next.invoice) p.invoice = next.invoice;
    if (next.send) p.send = '1';
    setParams(p);
  };

  const loadAll = useCallback(async () => {
    try {
      const s = await loadSeller();
      setSeller(s);
      if (!s) return;
      const [c, p, t, i] = await Promise.all([loadClients(s.id), loadProducts(s.id), loadTaxRates(s.id), loadInvoices(s.id)]);
      setClients(c); setProducts(p); setTaxRates(t); setInvoices(i);
    } catch (e) {
      setError(e.message);
      setSeller((x) => (x === undefined ? null : x));
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (error && !seller) return <div className={errorBox}>{error}</div>;
  if (seller === undefined) return <p className="text-ink-muted">Loading…</p>;
  if (!seller) return <div className={errorBox}>No business profile found. Run the invoicing seed migration.</div>;

  if (openId) {
    return (
      <InvoiceEditor key={openId} id={openId} autoSend={params.get('send') === '1'} seller={seller} clients={clients} products={products} taxRates={taxRates}
        onClose={() => { nav({ tab: 'list' }); loadAll(); }} onOpen={(id, send) => nav({ invoice: id, send })} onChanged={loadAll} />
    );
  }

  const profileMissing = !seller.address || !seller.email;
  return (
    <div className="space-y-5">
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => nav({ tab: k })}
            className={`h-10 px-3.5 text-sm -mb-px border-b-2 ${tab === k ? 'border-blue text-ink font-semibold' : 'border-transparent text-ink-muted hover:text-ink'}`}>
            {label}
          </button>
        ))}
      </div>
      {error && <div className={errorBox}>{error}</div>}
      {profileMissing && tab !== 'profile' && (
        <div className="bg-coral-soft border border-coral/40 text-ink px-4 py-3 rounded-lg text-sm">
          Your business profile is missing {[!seller.address && 'an address', !seller.email && 'an email'].filter(Boolean).join(' and ')}, which every invoice shows.{' '}
          <button type="button" onClick={() => nav({ tab: 'profile' })} className="font-semibold underline">Complete it</button>
        </div>
      )}
      {tab === 'list' && <InvoiceList invoices={invoices} clients={clients} onOpen={(id) => nav({ invoice: id })} />}
      {tab === 'clients' && <Clients seller={seller} clients={clients} onChanged={loadAll} />}
      {tab === 'products' && <Products seller={seller} products={products} onChanged={loadAll} />}
      {tab === 'taxes' && <Taxes seller={seller} taxRates={taxRates} onChanged={loadAll} />}
      {tab === 'reports' && <Reports seller={seller} invoices={invoices || []} />}
      {tab === 'profile' && <BusinessProfile seller={seller} onSaved={loadAll} />}
    </div>
  );
}

const FILTERS = [['open', 'Unpaid'], ['overdue', 'Overdue'], ['draft', 'Drafts'], ['paid', 'Paid'], ['void', 'Void'], ['all', 'All']];

function InvoiceList({ invoices, clients, onOpen }) {
  const [filter, setFilter] = useState('all');
  const [client, setClient] = useState('');
  const [q, setQ] = useState('');
  if (!invoices) return <p className="text-ink-muted">Loading invoices…</p>;
  const today = isoToday();
  const open = invoices.filter((i) => ['sent', 'partial'].includes(i.status));
  const outstanding = open.reduce((s, i) => s + i.total_cents - i.paid_cents, 0);
  const overdue = open.filter((i) => i.due_date < today).reduce((s, i) => s + i.total_cents - i.paid_cents, 0);
  const drafts = invoices.filter((i) => i.status === 'draft').length;
  const yearPaid = invoices.filter((i) => i.paid_at?.startsWith(today.slice(0, 4))).reduce((s, i) => s + i.paid_cents, 0);

  const term = q.trim().toLowerCase();
  const shown = invoices.filter((i) => {
    if (client && i.client_id !== client) return false;
    if (term && !`${i.number || ''} ${i.invoice_clients?.name || ''}`.toLowerCase().includes(term)) return false;
    switch (filter) {
      case 'open': return ['sent', 'partial'].includes(i.status);
      case 'overdue': return ['sent', 'partial'].includes(i.status) && i.due_date < today;
      case 'all': return true;
      default: return i.status === filter;
    }
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Outstanding" value={money(outstanding)} />
        <Tile label="Overdue" value={money(overdue)} bad={overdue > 0} />
        <Tile label={`Paid in ${today.slice(0, 4)}`} value={money(yearPaid)} />
        <Tile label="Drafts" value={drafts} />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Show"><select className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)}>{FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Client">
          <select className={inputClass} value={client} onChange={(e) => setClient(e.target.value)}>
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Search"><input className={inputClass} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Number or client" /></Field>
        <span className="flex-1" />
        <button type="button" className={btnPrimary} onClick={() => onOpen('new')}>New invoice</button>
      </div>
      <div className="bg-white rounded-2xl border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-ink-muted">
            <tr>
              {['Number', 'Client', 'Issued', 'Due', 'Status', 'Total', 'Balance'].map((h, i) => <th key={h} className={`px-4 py-3 font-medium ${i >= 5 ? 'text-right' : ''}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {shown.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-ink-muted">
                {invoices.length === 0 ? 'No invoices yet. Add a client and a product, then create your first invoice.' : 'No invoices match.'}
              </td></tr>
            )}
            {shown.map((i) => {
              const st = invoiceStatus(i);
              return (
                <tr key={i.id} onClick={() => onOpen(i.id)} className="cursor-pointer hover:bg-surface">
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(i.id); }} className="hover:underline">{i.number || 'Draft'}</button>
                  </td>
                  <td className="px-4 py-3">{i.invoice_clients?.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{shortDate(i.issue_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{shortDate(i.due_date)}</td>
                  <td className="px-4 py-3"><span className={`h-6 px-2.5 rounded-full text-xs font-semibold inline-flex items-center whitespace-nowrap ${STATUS_TONES[st.tone]}`}>{st.label}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(i.total_cents)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{['draft', 'void'].includes(i.status) ? '' : money(i.total_cents - i.paid_cents)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, bad }) {
  return (
    <div className="bg-white rounded-2xl border border-line p-4">
      <div className="text-sm text-ink-muted">{label}</div>
      <div className={`mt-1 text-[22px] font-extrabold tabular-nums ${bad ? 'text-coral-ink' : 'text-ink'}`}>{value}</div>
    </div>
  );
}

/* --------------------------------------------------------------- reports */

const quarterRange = (offset = 0) => {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + offset;
  const y = d.getFullYear() + Math.floor(q / 4);
  const qq = ((q % 4) + 4) % 4;
  const pad = (n) => String(n).padStart(2, '0');
  const lastDay = new Date(y, qq * 3 + 3, 0).getDate();
  return [`${y}-${pad(qq * 3 + 1)}-01`, `${y}-${pad(qq * 3 + 3)}-${pad(lastDay)}`];
};
const monthRange = (offset = 0) => {
  const d = new Date();
  const s = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  const e = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return [iso(s), iso(e)];
};
const PERIODS = {
  this_month: ['This month', () => monthRange(0)],
  last_month: ['Last month', () => monthRange(-1)],
  this_quarter: ['This quarter', () => quarterRange(0)],
  last_quarter: ['Last quarter', () => quarterRange(-1)],
  this_year: ['This year', () => [`${new Date().getFullYear()}-01-01`, `${new Date().getFullYear()}-12-31`]],
  last_year: ['Last year', () => [`${new Date().getFullYear() - 1}-01-01`, `${new Date().getFullYear() - 1}-12-31`]],
};

function Reports({ seller, invoices }) {
  const [period, setPeriod] = useState('last_month');
  const [range, setRange] = useState(() => PERIODS.last_month[1]());
  const [basis, setBasis] = useState('cash');
  const [tax, setTax] = useState(null);
  const [withLines, setWithLines] = useState(null);
  const [error, setError] = useState('');
  const [from, to] = range;

  useEffect(() => {
    setTax(null);
    salesTaxReport(seller.id, from, to, basis).then(setTax).catch((e) => setError(e.message));
  }, [seller.id, from, to, basis]);
  useEffect(() => {
    supabase.from('invoices').select('id, status, issue_date, subtotal_cents, discount_cents, invoice_lines(amount_cents, product_line)')
      .eq('seller_id', seller.id).then(({ data, error: err }) => (err ? setError(err.message) : setWithLines(data)));
  }, [seller.id]);

  const aging = useMemo(() => receivablesAging(invoices), [invoices]);
  const byLine = useMemo(() => (withLines ? revenueByLine(withLines, from, to) : null), [withLines, from, to]);
  const label = `${shortDate(from)} – ${shortDate(to)}`;

  const taxCsv = () => downloadCsv(
    ['Period from', 'Period to', 'Basis', 'Tax', 'Rate %', 'Tax collected (USD)'],
    [...tax.rates.map((r) => [from, to, basis, r.name, r.rate, r.cents / 100]),
      [from, to, basis, 'Taxable sales', '', tax.taxable / 100], [from, to, basis, 'Exempt / non-taxable sales', '', tax.exempt / 100]],
    `sales-tax-${from}-to-${to}.csv`,
  );

  return (
    <div className="space-y-6">
      {error && <div className={errorBox}>{error}</div>}
      <Card title="Money owed to you (A/R aging)">
        {aging.rows.length === 0 ? <p className="text-sm text-ink-muted">Nothing outstanding.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink-muted"><tr><th className="py-2 pr-4 font-medium">Client</th>{aging.buckets.map((b) => <th key={b} className="py-2 px-3 font-medium text-right whitespace-nowrap">{b}</th>)}<th className="py-2 pl-3 font-medium text-right">Total</th></tr></thead>
              <tbody className="divide-y divide-line tabular-nums">
                {aging.rows.map((r) => (
                  <tr key={r.name}><td className="py-2 pr-4">{r.name}</td>{r.cells.map((c, i) => <td key={i} className={`py-2 px-3 text-right ${i >= 2 && c ? 'text-coral-ink' : ''}`}>{c ? money(c) : ''}</td>)}<td className="py-2 pl-3 text-right font-semibold">{money(r.total)}</td></tr>
                ))}
                <tr className="font-semibold"><td className="py-2 pr-4">Total</td>{aging.totals.map((c, i) => <td key={i} className="py-2 px-3 text-right">{money(c)}</td>)}<td className="py-2 pl-3 text-right">{money(aging.total)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Period">
          <select className={inputClass} value={period} onChange={(e) => { setPeriod(e.target.value); if (PERIODS[e.target.value]) setRange(PERIODS[e.target.value][1]()); }}>
            {Object.entries(PERIODS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            <option value="custom">Custom</option>
          </select>
        </Field>
        {period === 'custom' && (
          <>
            <Field label="From"><input type="date" className={inputClass} value={from} onChange={(e) => e.target.value && setRange([e.target.value, to])} /></Field>
            <Field label="To"><input type="date" className={inputClass} value={to} onChange={(e) => e.target.value && setRange([from, e.target.value])} /></Field>
          </>
        )}
      </div>

      <Card title={`Sales tax · ${label}`} actions={tax && <button type="button" className={btnSecondary} onClick={taxCsv}>Download CSV</button>}>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="radio" checked={basis === 'cash'} onChange={() => setBasis('cash')} /> Cash basis (payments received in the period)</label>
          <label className="flex items-center gap-2"><input type="radio" checked={basis === 'accrual'} onChange={() => setBasis('accrual')} /> Accrual basis (invoices issued in the period)</label>
        </div>
        {!tax ? <p className="text-sm text-ink-muted">Loading…</p> : (
          <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm tabular-nums max-w-md">
            <dt className="text-ink-muted">Taxable sales</dt><dd className="text-right">{money(tax.taxable)}</dd>
            <dt className="text-ink-muted">Exempt / non-taxable sales</dt><dd className="text-right">{money(tax.exempt)}</dd>
            {tax.rates.map((r) => (<FragmentRow key={`${r.name}${r.rate}`} label={`${r.name} (${r.rate}%)`} value={money(r.cents)} />))}
            <dt className="font-bold border-t border-line pt-1">Sales tax collected</dt><dd className="font-bold text-right border-t border-line pt-1">{money(tax.tax)}</dd>
          </dl>
        )}
        <p className="text-xs text-ink-muted">Florida files sales tax (DR-15) on what you collect; check with your accountant whether you file on a cash or accrual basis. Stripe sales are handled by Stripe (Managed Payments) and are not included here.</p>
      </Card>

      <Card title={`Revenue by product line · ${label}`}>
        {!byLine ? <p className="text-sm text-ink-muted">Loading…</p> : byLine.length === 0 ? <p className="text-sm text-ink-muted">No invoices issued in this period.</p> : (
          <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-sm tabular-nums max-w-md">
            {byLine.map((r) => <FragmentRow key={r.name} label={r.name} value={money(r.cents)} />)}
            <dt className="font-bold border-t border-line pt-1">Total (before tax)</dt><dd className="font-bold text-right border-t border-line pt-1">{money(byLine.reduce((s, r) => s + r.cents, 0))}</dd>
          </dl>
        )}
      </Card>
    </div>
  );
}

function FragmentRow({ label, value }) {
  return (<><dt className="text-ink-muted">{label}</dt><dd className="text-right">{value}</dd></>);
}
